import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import { Box, Stack, Avatar, Typography } from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';

import { convertLocalTime } from 'src/utils/convertTime';

import { getUserAvatarUrl } from 'src/services/user.service';

import TextMessage from './TextMessage';
import MediaMessage from './MediaMessage';
import UpdateMessage from './UpdateMessage';

export default function Message({
  item,
  row,
  showTimeAnchor,
  showSenderInfo,
  isLastInGroup,
  statusesList,
  departmentUsers,
  fetchUser,
}) {
  const { id: currentUserId } = useAuth();
  const { message, messageType, sender, sentAt } = item;
  const [user, setUser] = useState(null);

  const isSelf = String(sender) === String(currentUserId);
  const isSystemType = messageType === 'UPDATE' || messageType === 'SYSTEM';

  useEffect(() => {
    let isMounted = true;
    if (sender && !isSelf && (messageType === 'MESSAGE' || messageType === 'FILE')) {
      fetchUser(sender).then((data) => {
        if (isMounted) setUser(data);
      });
    }
    return () => {
      isMounted = false;
    };
  }, [sender, messageType, fetchUser, isSelf]);

  const displayName = user?.givenName
    ? `${user.givenName} ${user.familyName}`
    : user?.username || '...';

  return (
    <Box
      sx={{
        width: '100%',
        // LOGIC: If next message is from same sender, hug it (0.4), otherwise space it (3.5)
        mb: isSystemType ? 1 : isLastInGroup ? 3.5 : 0.4,
        mt: showSenderInfo ? 2 : 0,
      }}
    >
      {showTimeAnchor && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 4 }}
        >
          <Box sx={{ flex: 1, height: '1.5px', bgcolor: 'divider' }} />
          <Typography
            variant="caption"
            sx={{ px: 3, color: 'text.disabled', fontWeight: 800 }}
          >
            {new Date(convertLocalTime(sentAt)).toLocaleString([], {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
          <Box sx={{ flex: 1, height: '1.5px', bgcolor: 'divider' }} />
        </Box>
      )}

      {isSystemType ? (
        <UpdateMessage
          item={item}
          statusesList={statusesList}
          departmentUsers={departmentUsers}
          fetchUser={fetchUser}
        />
      ) : (
        <Stack
          direction="row"
          spacing={1.5}
          justifyContent={isSelf ? 'flex-end' : 'flex-start'}
          sx={{ px: 2 }}
        >
          {!isSelf && (
            <Box sx={{ width: 32, flexShrink: 0 }}>
              {showSenderInfo && (
                <Avatar
                  src={
                    user?.avatarVersion
                      ? getUserAvatarUrl(user.id, user.avatarVersion)
                      : null
                  }
                  sx={{
                    width: 32,
                    height: 32,
                    border: '1px solid rgba(0,0,0,0.08)',
                    mt: 3.6,
                  }}
                />
              )}
            </Box>
          )}

          <Stack alignItems={isSelf ? 'flex-end' : 'flex-start'} sx={{ maxWidth: '85%' }}>
            {!isSelf && showSenderInfo && (
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  color: 'text.primary',
                  fontSize: '0.8rem',
                  mb: 0.5,
                  ml: 0.5,
                }}
              >
                {displayName}
              </Typography>
            )}

            {messageType === 'FILE' ? (
              <MediaMessage
                message={message}
                isPrimary={isSelf}
                ticketId={row.id}
                isLastInGroup={isLastInGroup}
              />
            ) : (
              <TextMessage
                message={message}
                isPrimary={isSelf}
                isLastInGroup={isLastInGroup}
              />
            )}
          </Stack>
        </Stack>
      )}
    </Box>
  );
}

Message.propTypes = {
  item: PropTypes.object.isRequired,
  row: PropTypes.object.isRequired,
  showTimeAnchor: PropTypes.bool,
  showSenderInfo: PropTypes.bool,
  isLastInGroup: PropTypes.bool,
  statusesList: PropTypes.array,
  departmentUsers: PropTypes.array,
  fetchUser: PropTypes.func.isRequired,
};
